import { clearMapFeatures } from '@app/store/actions.js';
import type { RootAction } from '@app/store/rootAction.js';
import type { RootState } from '@app/store/store.js';
import {
  creditedAttributions,
  densifyAlong,
  newElevationCredits,
  withElevationAttributions,
} from '@shared/elevation.js';
import { smoothElevation } from '@shared/elevationSmoothing.js';
import type { Dispatch } from 'redux';
import { isTrackLine } from '../trackSelection.js';
import {
  dataViewerDelete,
  dataViewerDeleteFeature,
  dataViewerExplodeTrack,
  dataViewerSetData,
  dataViewerSetRenderGeojson,
  dataViewerSimplify,
  dataViewerSplitTrack,
} from './actions.js';

/**
 * Lazily builds the densified render copy of the loaded track and caches it via
 * {@link dataViewerSetRenderGeojson}. Runs **only after a server elevation
 * override** (`elevationDecision === 'all'`): that is the one state where every
 * point is known to be DEM-derived, so inserting DEM-sampled points between
 * vertices adds no recorded-vs-DEM seam. A track's own recorded elevation is
 * left alone (even when full) rather than have DEM injected between its measured
 * points, as are *fill missing* and *keep recorded*. The culvert ditches of a
 * conditioned terrain model are filled here too. With nothing to subdivide and
 * nothing to fill it is a no-op, leaving consumers reading `trackGeojson`.
 */
export async function ensureRenderGeojson(
  getState: () => RootState,
  dispatch: Dispatch<RootAction>,
): Promise<void> {
  const { trackGeojson, renderTrackGeojson, elevationDecision } =
    getState().trackViewer;

  if (renderTrackGeojson || !trackGeojson || elevationDecision !== 'all') {
    return;
  }

  const lines = trackGeojson.features.filter(isTrackLine);

  if (lines.length === 0) {
    return;
  }

  // What answered, stamped onto the render lines so the chart credits the models
  // the profile it draws was sampled from.
  const credits = newElevationCredits();

  const densified = await Promise.all(
    lines.map((line) =>
      densifyAlong(
        line,
        getState,
        [
          dataViewerSetData,
          dataViewerDelete,
          dataViewerDeleteFeature,
          dataViewerSplitTrack,
          dataViewerExplodeTrack,
          dataViewerSimplify,
          clearMapFeatures,
        ],
        credits,
      ),
    ),
  );

  const smoothed = densified.map((line) =>
    smoothElevation(line, getState().elevationSettings),
  );

  // Nothing was long enough to subdivide and no ditch needed filling → leave
  // the cache empty.
  if (smoothed.every((line, i) => line === lines[i])) {
    return;
  }

  // The track may have been replaced (or the cache filled by a concurrent call)
  // while sampling.
  const after = getState().trackViewer;

  if (after.trackGeojson !== trackGeojson || after.renderTrackGeojson) {
    return;
  }

  const reported = creditedAttributions(credits);

  let i = 0;

  const features = trackGeojson.features.map((f) =>
    isTrackLine(f) ? withElevationAttributions(smoothed[i++]!, reported) : f,
  );

  dispatch(dataViewerSetRenderGeojson({ ...trackGeojson, features }));
}
